import { Component, Injectable, Input, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SfoUiJSONSchema7 } from '../../../../metadata.model';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';

import * as _moment from 'moment';
import { default as _rollupMoment } from 'moment';
import { MatFormField, MatLabel, MatHint, MatSuffix } from '@angular/material/select';
import { MatError } from '@angular/material/form-field';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';

import { MatInput } from '@angular/material/input';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';

const moment = _rollupMoment || _moment;

/** The moment adapter, reading a date with the spaces around it trimmed (a paste often carries one). */
@Injectable()
export class TrimmingMomentDateAdapter extends MomentDateAdapter {
  override parse(value: any, parseFormat: string | string[]) {
    return super.parse(typeof value === 'string' ? value.trim() : value, parseFormat);
  }
}

/** ISO dates; the short form 2026-11-3 is read too. */
export const SFO_ISO_DATE_FORMATS = {
  parse: { dateInput: ['YYYY-MM-DD', 'YYYY-M-D'] },
  display: {
    dateInput: 'YYYY-MM-DD',
    monthYearLabel: 'YYYY-MM',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'YYYY-MM',
  },
};

@Component({
  selector: 'sfo-date-input',
  templateUrl: './date-input.component.html',
  // Bounded content — cap the width so dates don't span the full form.
  styles: ['mat-form-field { max-width: 14rem; }'],
  // The field brings its own STRICT date adapter, whatever the host form provides. The datepicker
  // parses the text on every key; a lenient parser reads the first key "2" as the year 2, and a
  // date typed key by key was stored as 0002-01-01. Strictly, a half-typed date is no date: the
  // control holds null with a `matDatepickerParse` error, the form is invalid and hands nothing on
  // until the whole date is there.
  providers: [
    { provide: DateAdapter, useClass: TrimmingMomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { strict: true } },
    { provide: MAT_DATE_FORMATS, useValue: SFO_ISO_DATE_FORMATS },
  ],
  imports: [
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    FormsModule,
    ReactiveFormsModule,
    MatHint,
    MatError,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
  ],
})
export class DateInputComponent implements OnInit {
  @Input() schema!: SfoUiJSONSchema7;
  @Input() aFormControl!: FormControl;
  isTouchDevice: boolean = false;

  constructor(private breakpointObserver: BreakpointObserver) {}

  ngOnInit(): void {
    this.breakpointObserver.observe(['(pointer: coarse)']).subscribe((state: BreakpointState) => {
      this.isTouchDevice = state.matches;
    });

    this.aFormControl.valueChanges.subscribe((value) => {
      // The active datepicker adapter writes either a Moment (moment adapter, e.g.
      // Storybook) OR a native Date (NativeDateAdapter — the publish module imports
      // MatNativeDateModule) into the control. Normalise EITHER to an ISO 'YYYY-MM-DD'
      // string and re-emit (default emitEvent:true) so the value reaching the form model /
      // save path is the string the schema expects — not a Moment/Date object, which the
      // save path can't persist. The guard is self-terminating: the resulting string is
      // neither a Moment nor a Date, so the re-emission pass is ignored (no loop).
      // `emitModelToViewChange: false`: the text the person is typing is never rewritten under
      // them (reading "2026-11-3" must not turn the field into "2026-11-03" while they type
      // "2026-11-30"); the datepicker shows its ISO form on blur.
      if (value && (moment.isMoment(value) || value instanceof Date)) {
        this.aFormControl.setValue(moment(value).format('YYYY-MM-DD'), { emitModelToViewChange: false });
      }
    });
  }
}
